import React, { useState } from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Theme,
    Tooltip,
} from '@material-ui/core'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import { MonetizationOn, InfoOutlined } from '@material-ui/icons'

import {
    APPROVE_DENY_CHANGE_ORDER,
    BaseWorkorder,
    ChangeOrder,
    ChangeOrderConfig,
    ChangeOrderStatus,
    SEE_VENDOR_FINANCES,
    WorkSpaceUser,
    WorkorderStatus,
    getChangeOrderColor,
} from '../../models'
import { Container } from '../Container'
import { Attachment } from '../Message'
import { ChangeOrderConfirmDeny } from './ChangeOrderConfirmDeny'
import { hasPermission } from '../../models/Users/services'
import { currencyFormatter } from '../../helpers'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    changeOrder: ChangeOrder
    theme: Theme
    changeOrderConfig?: ChangeOrderConfig
    workspaceUser?: WorkSpaceUser
    approveDenyChangeOrder: (
        changeOrder: ChangeOrder,
        status: ChangeOrderStatus,
    ) => Promise<void>
    deleteChangeOrder: (changeOrder: ChangeOrder) => Promise<void>
    parentWorkorder?: BaseWorkorder
}

export const ChangeOrderRow = (props: Props) => {
    const {
        changeOrder,
        theme,
        changeOrderConfig,
        workspaceUser,
        approveDenyChangeOrder,
        deleteChangeOrder,
        parentWorkorder,
    } = props

    const [openDialog, setOpenDialog] = useState(false)

    let price = changeOrderConfig?.suggested_add_price
    if (changeOrder.price) {
        price = changeOrder.price
    }

    const changeOrderTotalCost = changeOrder.quantity * (price ?? 0)

    let costString = ''
    if (hasPermission(workspaceUser, SEE_VENDOR_FINANCES)) {
        costString = `Cost: $${price} x ${
            changeOrder.quantity
        } = ${currencyFormatter.format(changeOrderTotalCost)}`
    }

    const workorderStatus =
        changeOrder.workorder?.status ?? parentWorkorder?.status
    const lockedWorkorderStatuses = [
        WorkorderStatus.APPROVED,
        WorkorderStatus.INVOICE_REQUEST,
        WorkorderStatus.INVOICED,
        WorkorderStatus.INVOICED_OUT,
    ]
    const canDeleteChangeOrder =
        workorderStatus != null &&
        !lockedWorkorderStatuses.includes(workorderStatus)

    const configDescription = (
        changeOrder.config_description ??
        changeOrderConfig?.description ??
        ''
    ).trim()
    const createdByName = changeOrder.created_by?.name
    const handledByName = changeOrder.handled_by?.name
    const isHandled =
        changeOrder.status === ChangeOrderStatus.APPROVED ||
        changeOrder.status === ChangeOrderStatus.DENIED

    const captionStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.caption),
        color: theme.palette.text.secondary,
    }

    return (
        <Container
            style={{
                margin: theme.spacing(1),
                backgroundColor: theme.palette.grey[100],
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.grey[600]}`,
                minHeight: 200,
                alignItems: 'stretch',
                overflow: 'visible',
                flexShrink: 0,
            }}
        >
            {/* Left Column */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    minWidth: LEFT_COL_W,
                    maxWidth: LEFT_COL_W,
                    flexDirection: 'column',
                    flexShrink: 0,
                    minHeight: 200,
                }}
            >
                <Container style={{ alignItems: 'center' }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 700,
                        }}
                    >
                        {changeOrderConfig?.name}
                    </span>
                    {configDescription && (
                        <Tooltip title={configDescription}>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    marginLeft: theme.spacing(0.5),
                                    cursor: 'pointer',
                                }}
                            >
                                <InfoOutlined fontSize="small" color="action" />
                            </span>
                        </Tooltip>
                    )}
                    <span style={{ marginLeft: theme.spacing(1) }}>
                        x {changeOrder.quantity}
                    </span>
                </Container>

                <Container style={{ alignItems: 'center' }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {changeOrder.location?.path ?? ''}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            {changeOrder.location?.folder_name ?? ''}
                        </span>
                    </span>
                </Container>

                <Container>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                        }}
                    >
                        {changeOrder.location?.unit_name ?? ''}
                    </span>
                </Container>

                <Container>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 500,
                        }}
                    >
                        {changeOrder.vendor?.name ?? ''}
                    </span>
                </Container>

                <span
                    style={{
                        ...captionStyle,
                        wordBreak: 'break-word',
                        marginTop: theme.spacing(0.5),
                    }}
                >
                    Created
                    {createdByName ? ` by ${createdByName}` : ''}
                    {' · '}
                    {new Date(changeOrder.created_date).toLocaleDateString()}
                </span>

                {isHandled && (handledByName || changeOrder.handled_at) && (
                    <span
                        style={{
                            ...captionStyle,
                            wordBreak: 'break-word',
                        }}
                    >
                        {changeOrder.status}
                        {handledByName ? ` by ${handledByName}` : ''}
                        {changeOrder.handled_at
                            ? ` · ${new Date(
                                  changeOrder.handled_at,
                              ).toLocaleDateString()}`
                            : ''}
                    </span>
                )}

                {(changeOrder.workorder?.id ?? parentWorkorder?.id) && (
                    <span
                        style={{
                            ...captionStyle,
                            wordBreak: 'break-word',
                        }}
                    >
                        Work order #
                        {changeOrder.workorder?.id ?? parentWorkorder?.id}
                    </span>
                )}

                <Container
                    style={{
                        marginTop: 'auto',
                        paddingTop: theme.spacing(1),
                        alignItems: 'center',
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            backgroundColor: getChangeOrderColor(
                                changeOrder.status,
                                theme,
                            ),
                            color: 'white',
                            borderRadius: theme.shape.borderRadius,
                            borderWidth: 1,
                            borderColor: getChangeOrderColor(
                                changeOrder.status,
                                theme,
                            ),
                            padding: theme.spacing(1),
                            width: 80,
                            textAlign: 'center',
                        }}
                    >
                        {changeOrder.status}
                    </span>
                    <div style={{ marginLeft: theme.spacing(2) }} />
                    {changeOrder.status === ChangeOrderStatus.PENDING &&
                        hasPermission(
                            workspaceUser,
                            APPROVE_DENY_CHANGE_ORDER,
                        ) && (
                            <ChangeOrderConfirmDeny
                                changeOrder={changeOrder}
                                approveDenyChangeOrder={approveDenyChangeOrder}
                            />
                        )}
                    <div style={{ flex: 1 }} />
                    {canDeleteChangeOrder && (
                        <Tooltip title="Delete">
                            <IconButton
                                onClick={() => setOpenDialog(true)}
                                size="small"
                            >
                                <DeleteForeverIcon color="secondary" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Container>
            </Container>

            {/* Middle Column: Description */}
            <Container
                style={{
                    borderLeft: `1px solid ${theme.palette.grey[600]}`,
                    borderRight: `1px solid ${theme.palette.grey[600]}`,
                    minWidth: 300,
                    maxWidth: 300,
                    flexDirection: 'column',
                    fontWeight: 500,
                }}
            >
                <Container
                    style={{
                        flex: 2,
                        borderBottom: `1px solid ${theme.palette.grey[600]}`,
                        padding: theme.spacing(1),
                        overflow: 'auto',
                    }}
                >
                    <span>{changeOrder.description}</span>
                </Container>
                <Container
                    style={{
                        flex: 1,
                        padding: theme.spacing(1),
                    }}
                >
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                        }}
                    >
                        <span style={{ height: 20 }}>{costString}</span>
                    </Container>
                    {changeOrder.is_custom_price && (
                        <Container
                            style={{
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Tooltip title="Custom Price">
                                <MonetizationOn
                                    htmlColor={theme.palette.primary.dark}
                                />
                            </Tooltip>
                        </Container>
                    )}
                </Container>
            </Container>

            {/* Right Column: Attachments */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    paddingBottom: 0,
                    paddingRight: 0,
                    alignItems: 'center',
                    overflowX: 'auto',
                    minWidth: 0,
                    flex: 1,
                }}
            >
                {changeOrder.image_0 && (
                    <Attachment
                        attachment={changeOrder.image_0}
                        uploaded={changeOrder.image_0_uploaded}
                        theme={theme}
                        size={180}
                    />
                )}
                {changeOrder.image_1 && (
                    <Attachment
                        attachment={changeOrder.image_1}
                        uploaded={changeOrder.image_1_uploaded}
                        theme={theme}
                        size={180}
                    />
                )}
                {changeOrder.image_2 && (
                    <Attachment
                        attachment={changeOrder.image_2}
                        uploaded={changeOrder.image_2_uploaded}
                        theme={theme}
                        size={180}
                    />
                )}
            </Container>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle id="alert-dialog-title">
                    Delete Change Order
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this Change Order?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => setOpenDialog(false)}
                    >
                        Cancel
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                            deleteChangeOrder(changeOrder)
                                .then(() => {
                                    setOpenDialog(false)
                                })
                                .catch(() => undefined)
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

const LEFT_COL_W = 420
